import React from 'react';
import { withRouter } from 'react-router-dom';
import { auth } from '../../../Api';
import {
	COMPANY_SUSPENSION_EVENT,
	getCompanySuspended,
	clearCompanySuspended,
} from '../../../utils/session';
import './suspension.scss';

// Where a suspended company's own admin is kept: the Account page, on its Billing tab
export const ACCOUNT_PATH = '/account';
export const SUSPENDED_HOME = { pathname: ACCOUNT_PATH, search: '?tab=billing' };

// The banner's text. Only a suspended company's own admin sees it, and that admin is the one who can pay,
// so it differs on purpose from the server's message ("... try again later"), which the phone apps, district
// managers and resellers keep. The value stored under "company_suspended" is only the on/off flag.
export const COMPANY_ADMIN_SUSPENDED_MESSAGE = 'Your company account is currently suspended. Please settle the outstanding invoice';

/**
 * True while the signed-in company admin's company is suspended.
 */
export function isCompanySuspended() {
	return getCompanySuspended() !== null && localStorage.getItem('admin_level') === 'c';
}

/**
 * True when this page must not open now and the user goes to SUSPENDED_HOME instead
 * (layouts/landingLayout.js renders a Redirect in its place).
 */
export function mustRedirectToAccount(pathname) {
	return isCompanySuspended() && pathname !== ACCOUNT_PATH;
}

/**
 * Banner for a suspended company's own admin (level c), who stays signed in so it can pay.
 * Rendered at the top of every page by layouts/landingLayout.js.
 *
 * The suspended state is set by Api/BaseApi.js (and the login page) when the server answers code 423 or
 * "suspended": true. On each page change the company's own details are asked for again (a route the
 * server allows while suspended), and again when the browser tab becomes visible (back from paying in the
 * Stripe tab): a reply without "suspended" means the company was reinstated, so the state is cleared, the
 * banner goes away and the menu and pages work again (layouts/landingLayout.js and the sidebar follow the
 * state's event).
 */
class SuspensionBanner extends React.Component {
	constructor(props) {
		super(props);
		this.state = { suspended: getCompanySuspended() !== null };
		this.onChange = this.onChange.bind(this);
		this.onVisible = this.onVisible.bind(this);
	}

	componentDidMount() {
		window.addEventListener(COMPANY_SUSPENSION_EVENT, this.onChange);
		this.unlisten = this.props.history.listen(() => this.recheck());
		document.addEventListener('visibilitychange', this.onVisible);
		this.recheck();
	}

	componentWillUnmount() {
		window.removeEventListener(COMPANY_SUSPENSION_EVENT, this.onChange);
		document.removeEventListener('visibilitychange', this.onVisible);
		if (this.unlisten) {
			this.unlisten();
		}
	}

	onChange() {
		this.setState({ suspended: getCompanySuspended() !== null });
	}

	onVisible() {
		if (document.visibilityState === 'visible') {
			this.recheck();
		}
	}

	recheck() {
		if (!isCompanySuspended()) {
			return;
		}
		auth.getCompany({ id: localStorage.getItem('admin_id') })
			.then((res) => {
				// Still suspended: the reply says so and BaseApi keeps the state
				if (res && res.data && res.data.code == 200 && res.data.suspended !== true) {
					clearCompanySuspended();
				}
			})
			.catch(() => {});
	}

	render() {
		if (!this.state.suspended || localStorage.getItem('admin_level') !== 'c') {
			return null;
		}
		return (
			<div className="alert alert-danger suspension-banner" role="alert">
				<i className="mdi mdi-alert-circle mr-10"></i>
				{COMPANY_ADMIN_SUSPENDED_MESSAGE}
			</div>
		);
	}
}

export default withRouter(SuspensionBanner);
